import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { normalizeApiError } from "@/shared/api/client";
import { Button } from "@/shared/ui/Button";
import {
  fetchMe,
  getCurrentUser,
  beginAuthSessionMutation,
  cancelAuthSessionMutation,
  captureAuthSessionOwner,
  isAuthSessionMutationCurrent,
  isAuthSessionOwnerCurrent,
  isSignedIn,
  readContactSignInLinkApproval,
  readPasswordChangeLinkApproval,
  readVerificationLinkPurpose,
  verifyOtpLink,
  type AuthSessionOwner,
  type AuthSessionTransitionPermit,
  type AuthUser,
} from "./session";
import { useAuth } from "./AuthProvider";
import { applyAuthenticatedOrCreatedAccountSession } from "./created-account-session";
import { captureCurrentAnonymousAccountStorageMigration } from "@/shared/storage/account-scope";
import {
  createLatestVerificationLinkRequestGate,
  type LatestVerificationLinkRequestGate,
} from "./latest-verification-link-request.mjs";
import {
  createPasswordResetNavigationState,
  readPasswordResetLinkApproval,
} from "./password-reset-link.mjs";
import {
  isCrossOriginAuthExit,
  passwordResetAuthExitDestination,
} from "./reset-origin-navigation.mjs";
import {
  readOwnedClearedVerificationLinkCompletion,
  resolveVerificationLinkCompletionForPurpose,
} from "./verification-link-completion.mjs";
import { localizedAuthError } from "./localized-auth-error";
import { publicWebCopyForLang, type PublicWebCopyKey } from "@/shared/i18n/public-web-copy";
import { useMarketingExperience } from "@/shared/i18n/use-product-locale";
import "./VerifyLinkOverlay.css";

const PERMANENT_VERIFY_LINK_FAILURES = new Set([
  "invalid_verification_link",
  "invalid_otp",
  "otp_target_mismatch",
  "verification_user_required",
  "missing_user",
  "invalid_identity",
  "invalid_verification_identity",
  "invalid_contact_sign_in_response",
  "invalid_password_change_link_response",
  "invalid_password_reset_link_response",
  "invalid_auth_session_response",
  "invalid_auth_token_response",
  "invalid_contact_sign_in_challenge",
  "contact_sign_in_challenge_expired",
  "contact_sign_in_device_mismatch",
  "contact_already_verified",
  "credential_version_mismatch",
  "current_contact_verification_required",
  "identity_change_user_mismatch",
  "password_reset_link_expired",
  "password_reset_already_used",
]);

type VerifyLinkPhase =
  | { kind: "idle" }
  | { kind: "verifying"; token: string }
  | {
      kind: "success";
      user: AuthUser | null;
      canOpenBrief: boolean;
      signInApproved: boolean;
      identityChanged: boolean;
    }
  | { kind: "password_reset"; resetToken: string }
  | { kind: "password_change_approved" }
  | { kind: "error"; message: string; code: string; retryToken: string | null };

type VerifyLinkContextValue = {
  phase: VerifyLinkPhase;
  close: () => void;
  startVerification: (token: string) => Promise<void>;
};

const VerifyLinkContext = createContext<VerifyLinkContextValue | null>(null);

type AuthCopy = (
  key: PublicWebCopyKey,
  vars?: Record<string, string | number | undefined | null>,
) => string;

function verifyLinkErrorMessage(
  err: unknown,
  linkPurpose: ReturnType<typeof readVerificationLinkPurpose>,
  copy: AuthCopy,
): string {
  const error = normalizeApiError(err, "verify_link_failed");
  if (
    linkPurpose === "reset" &&
    (error.code === "invalid_verification_link" ||
      error.code === "invalid_otp" ||
      error.code === "credential_version_mismatch" ||
      error.code === "invalid_password_reset_link_response" ||
      error.code === "password_reset_link_expired" ||
      error.code === "password_reset_already_used")
  ) {
    return copy("auth.error.resetLinkInvalid");
  }
  if (
    linkPurpose === "password_change" &&
    (error.code === "invalid_verification_link" ||
      error.code === "invalid_otp" ||
      error.code === "credential_version_mismatch" ||
      error.code === "invalid_password_change_link_response")
  ) {
    return copy("auth.error.passwordChangeLinkInvalid");
  }
  if (error.code === "invalid_verification_link") {
    return copy("auth.error.verificationLinkInvalid");
  }
  if (error.code === "verification_user_required" || error.code === "missing_user") {
    return copy("auth.error.verificationUserRequired");
  }
  return localizedAuthError(error, copy);
}

function invalidAuthSessionResponse(): Error & { code: string } {
  const error = new Error("invalid_auth_session_response") as Error & {
    code: string;
  };
  error.code = "invalid_auth_session_response";
  return error;
}

function invalidPasswordChangeLinkResponse(): Error & { code: string } {
  const error = new Error("invalid_password_change_link_response") as Error & {
    code: string;
  };
  error.code = "invalid_password_change_link_response";
  return error;
}

export function VerifyLinkProvider({ children }: { children: ReactNode }) {
  const experience = useMarketingExperience();
  const copy = useMemo(() => publicWebCopyForLang(experience.lang), [experience.lang]);
  const { onTokenResponse, setUser } = useAuth();
  const [phase, setPhase] = useState<VerifyLinkPhase>({ kind: "idle" });
  const requestGateRef = useRef<LatestVerificationLinkRequestGate | null>(null);
  const activeTransitionRef = useRef<AuthSessionTransitionPermit | null>(null);
  if (!requestGateRef.current) {
    requestGateRef.current = createLatestVerificationLinkRequestGate();
  }

  const close = useCallback(() => {
    requestGateRef.current?.cancelAll();
    if (activeTransitionRef.current) {
      cancelAuthSessionMutation(activeTransitionRef.current);
      activeTransitionRef.current = null;
    }
    setPhase({ kind: "idle" });
  }, []);

  const startVerification = useCallback(
    async (token: string) => {
      const request = requestGateRef.current?.start(token);
      if (!request) return;
      const linkPurpose = readVerificationLinkPurpose(request.token);
      const migrationIntent =
        linkPurpose === "verification" ? captureCurrentAnonymousAccountStorageMigration() : null;
      const contactSignInApproval = linkPurpose === "contact_sign_in";
      const passwordResetApproval = linkPurpose === "reset";
      const passwordChangeApproval = linkPurpose === "password_change";
      const identityChangeOwner =
        linkPurpose === "identity_change" ? captureAuthSessionOwner() : null;
      const identityChangePrimaryType =
        linkPurpose === "identity_change" ? getCurrentUser()?.verificationIdentityType : undefined;
      const verificationOwner = linkPurpose === "verification" ? captureAuthSessionOwner() : null;
      const verificationPrimaryType =
        linkPurpose === "verification" ? getCurrentUser()?.verificationIdentityType : undefined;
      const permit =
        contactSignInApproval || passwordResetApproval || passwordChangeApproval
          ? null
          : beginAuthSessionMutation("verification-link");
      if (permit) activeTransitionRef.current = permit;
      setPhase({ kind: "verifying", token: request.token });
      try {
        if (!linkPurpose) throw invalidAuthSessionResponse();
        if (
          linkPurpose === "identity_change" &&
          (!identityChangeOwner || identityChangePrimaryType === undefined)
        ) {
          throw invalidAuthSessionResponse();
        }
        const data = await verifyOtpLink({ token: request.token, rememberMe: true });
        if (!requestGateRef.current?.isLatest(request)) return;

        if (passwordResetApproval) {
          if (!readPasswordResetLinkApproval(data)) {
            throw invalidAuthSessionResponse();
          }
          setPhase({ kind: "password_reset", resetToken: request.token });
          return;
        }

        if (passwordChangeApproval) {
          if (!readPasswordChangeLinkApproval(data)) {
            throw invalidPasswordChangeLinkResponse();
          }
          setPhase({ kind: "password_change_approved" });
          return;
        }

        if (linkPurpose === "identity_change") {
          if (!permit || !isAuthSessionMutationCurrent(permit) || !identityChangeOwner) return;
          const completion = resolveVerificationLinkCompletionForPurpose(
            linkPurpose,
            data,
            () => false,
          );
          if (completion?.kind !== "identity_changed") {
            throw invalidAuthSessionResponse();
          }
          const completionUser = completion.user as AuthUser;
          const completionUserId = String(completionUser.userId || completionUser.id || "").trim();
          const completionPhone = String(
            completionUser.phoneE164 || completionUser.phone || "",
          ).trim();
          if (
            completionUserId !== identityChangeOwner.userId ||
            completionUser.verificationIdentityType !== identityChangePrimaryType ||
            !isAuthSessionOwnerCurrent(identityChangeOwner)
          ) {
            throw invalidAuthSessionResponse();
          }
          const verifiedUser = await fetchMe({
            expectedVerificationIdentityType: identityChangePrimaryType,
          });
          if (
            !requestGateRef.current?.isLatest(request) ||
            !isAuthSessionMutationCurrent(permit) ||
            !isAuthSessionOwnerCurrent(identityChangeOwner)
          ) {
            return;
          }
          const verifiedUserId = String(verifiedUser?.userId || verifiedUser?.id || "").trim();
          const verifiedPhone = String(verifiedUser?.phoneE164 || verifiedUser?.phone || "").trim();
          if (
            verifiedUserId !== completionUserId ||
            verifiedPhone !== completionPhone ||
            verifiedUser?.phoneVerified !== true
          ) {
            throw invalidAuthSessionResponse();
          }
          setUser(verifiedUser);
          setPhase({
            kind: "success",
            user: verifiedUser,
            canOpenBrief: true,
            signInApproved: false,
            identityChanged: true,
          });
          return;
        }

        const signInApproval = readContactSignInLinkApproval(data);
        if (contactSignInApproval) {
          if (!signInApproval) throw invalidAuthSessionResponse();
          setPhase({
            kind: "success",
            user: null,
            canOpenBrief: false,
            signInApproved: true,
            identityChanged: false,
          });
          return;
        }
        if (signInApproval) throw invalidAuthSessionResponse();
        if (!permit || !isAuthSessionMutationCurrent(permit)) return;
        const completion = resolveVerificationLinkCompletionForPurpose(linkPurpose, data, () =>
          applyAuthenticatedOrCreatedAccountSession(data, permit, migrationIntent, onTokenResponse),
        );
        const sessionApplied = completion?.kind === "authenticated";
        let clearedOwner: AuthSessionOwner | null = null;
        if (!sessionApplied) {
          if (completion?.kind === "signed_out") {
            setPhase({
              kind: "success",
              user: null,
              canOpenBrief: false,
              signInApproved: false,
              identityChanged: false,
            });
            return;
          }
          if (
            completion?.kind !== "cleared" ||
            !verificationOwner ||
            verificationPrimaryType === undefined ||
            !isAuthSessionOwnerCurrent(verificationOwner) ||
            !isSignedIn()
          ) {
            throw invalidAuthSessionResponse();
          }
          readOwnedClearedVerificationLinkCompletion(
            completion,
            verificationOwner.userId,
            verificationPrimaryType,
          );
          clearedOwner = verificationOwner;
        }
        const verifiedUser = await fetchMe(
          clearedOwner ? { expectedVerificationIdentityType: verificationPrimaryType } : undefined,
        );
        if (
          !requestGateRef.current?.isLatest(request) ||
          !isAuthSessionMutationCurrent(permit) ||
          (clearedOwner && !isAuthSessionOwnerCurrent(clearedOwner))
        ) {
          return;
        }
        setUser(verifiedUser);
        setPhase({
          kind: "success",
          user: verifiedUser,
          canOpenBrief: sessionApplied || isSignedIn(),
          signInApproved: false,
          identityChanged: false,
        });
      } catch (err) {
        if (
          !requestGateRef.current?.isLatest(request) ||
          (permit && !isAuthSessionMutationCurrent(permit))
        ) {
          return;
        }
        const error = normalizeApiError(err, "verify_link_failed");
        setPhase({
          kind: "error",
          code: error.code || "verify_link_failed",
          message: verifyLinkErrorMessage(error, linkPurpose, copy),
          retryToken: PERMANENT_VERIFY_LINK_FAILURES.has(error.code) ? null : request.token,
        });
      } finally {
        requestGateRef.current?.finish(request);
        if (permit && activeTransitionRef.current === permit) {
          cancelAuthSessionMutation(permit);
          activeTransitionRef.current = null;
        }
      }
    },
    [copy, onTokenResponse, setUser],
  );

  return (
    <VerifyLinkContext.Provider value={{ phase, close, startVerification }}>
      {children}
    </VerifyLinkContext.Provider>
  );
}

export function useVerifyLinkOverlay() {
  const ctx = useContext(VerifyLinkContext);
  if (!ctx) throw new Error("useVerifyLinkOverlay outside VerifyLinkProvider");
  return ctx;
}

export function VerifyLinkRootShell() {
  const { phase } = useVerifyLinkOverlay();
  const routeContentRef = useRef<HTMLDivElement | null>(null);
  const dialogOpen = phase.kind !== "idle";

  useEffect(() => {
    const routeContent = routeContentRef.current;
    if (!routeContent) return;
    routeContent.inert = dialogOpen;
    if (dialogOpen) routeContent.setAttribute("aria-hidden", "true");
    else routeContent.removeAttribute("aria-hidden");
    return () => {
      routeContent.inert = false;
      routeContent.removeAttribute("aria-hidden");
    };
  }, [dialogOpen]);

  return (
    <>
      <div className="verify-link-route-content" ref={routeContentRef}>
        <Outlet />
      </div>
      <WhatsAppVerifyLinkOverlay />
    </>
  );
}

export function WhatsAppVerifyLinkOverlay() {
  const experience = useMarketingExperience();
  const copy = useMemo(() => publicWebCopyForLang(experience.lang), [experience.lang]);
  const { phase, close, startVerification } = useVerifyLinkOverlay();
  const navigate = useNavigate();
  const currentOrigin = window.location.origin;
  const signInDestination = passwordResetAuthExitDestination("/auth/sign-in", currentOrigin);
  const cardRef = useRef<HTMLElement | null>(null);
  const dialogOpen = phase.kind !== "idle";
  const isVerifying = phase.kind === "verifying";
  const isStandardSuccess = phase.kind === "success";
  const passwordResetReady = phase.kind === "password_reset";
  const passwordChangeApproved = phase.kind === "password_change_approved";
  const isSuccess = isStandardSuccess || passwordResetReady || passwordChangeApproved;
  const canOpenBrief = isStandardSuccess && phase.canOpenBrief;
  const signInApproved = isStandardSuccess && phase.signInApproved;
  const identityChanged = isStandardSuccess && phase.identityChanged;
  const goToSignIn = useCallback(() => {
    if (isCrossOriginAuthExit(signInDestination, currentOrigin)) {
      window.location.replace(signInDestination);
    } else {
      navigate(signInDestination, { replace: true });
    }
  }, [currentOrigin, navigate, signInDestination]);
  const dismiss = useCallback(() => {
    close();
    if (passwordResetReady) {
      goToSignIn();
    }
  }, [close, goToSignIn, passwordResetReady]);

  useEffect(() => {
    if (!dialogOpen) return;
    const priorFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    cardRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      const card = cardRef.current;
      if (!card) return;
      if (event.key === "Escape") {
        event.preventDefault();
        if (!isVerifying) dismiss();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(
        card.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (!focusable.length) {
        event.preventDefault();
        card.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      priorFocus?.focus();
    };
  }, [dialogOpen, dismiss, isVerifying]);

  if (phase.kind === "idle") return null;

  const title = isVerifying
    ? copy("auth.overlay.verifyingTitle")
    : isSuccess
      ? passwordResetReady
        ? copy("auth.overlay.resetTitle")
        : passwordChangeApproved
          ? copy("auth.overlay.passwordChangeTitle")
          : identityChanged
            ? copy("auth.overlay.mobileUpdatedTitle")
            : signInApproved
              ? copy("auth.overlay.signInApprovedTitle")
              : copy("auth.overlay.phoneVerifiedTitle")
      : copy("auth.overlay.attentionTitle");
  const detail = isVerifying
    ? copy("auth.overlay.verifyingDetail")
    : isSuccess
      ? passwordResetReady
        ? copy("auth.overlay.resetDetail")
        : passwordChangeApproved
          ? copy("auth.overlay.passwordChangeDetail")
          : identityChanged
            ? canOpenBrief
              ? copy("auth.overlay.identityOpenDetail")
              : copy("auth.overlay.identityReturnDetail")
            : signInApproved
              ? copy("auth.overlay.signInDetail")
              : canOpenBrief
                ? copy("auth.overlay.accountReadyDetail")
                : copy("auth.overlay.phoneVerifiedDetail")
      : phase.message;

  return (
    <div className="verify-link-overlay" role="presentation">
      <section
        ref={cardRef}
        className={`verify-link-card verify-link-card--${phase.kind}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="verify-link-title"
        aria-describedby="verify-link-detail"
        aria-busy={isVerifying}
        tabIndex={-1}
      >
        <div className="verify-link-hero" aria-hidden="true">
          {isVerifying ? <span className="verify-link-spinner" /> : null}
          {isSuccess ? <span className="verify-link-glyph">✓</span> : null}
          {phase.kind === "error" ? <span className="verify-link-glyph">!</span> : null}
        </div>
        <div className="verify-link-copy">
          <p className="verify-link-kicker">
            {passwordResetReady
              ? copy("auth.overlay.passwordResetKicker")
              : passwordChangeApproved
                ? copy("auth.overlay.passwordSecurityKicker")
                : copy("auth.overlay.whatsAppKicker")}
          </p>
          <h2 id="verify-link-title">{title}</h2>
          <p id="verify-link-detail">{detail}</p>
        </div>
        <div className="verify-link-actions">
          {phase.kind === "error" && phase.retryToken ? (
            <Button
              variant="primary"
              onClick={() => void startVerification(phase.retryToken || "")}
            >
              {copy("auth.google.tryAgain")}
            </Button>
          ) : null}
          {canOpenBrief ? (
            <Button
              variant="primary"
              onClick={() => {
                close();
                navigate("/app/brief", { replace: true });
              }}
            >
              {copy("auth.verify.openBrief")}
            </Button>
          ) : null}
          {passwordResetReady ? (
            <Button
              variant="primary"
              onClick={() => {
                const state = createPasswordResetNavigationState(phase.resetToken);
                close();
                navigate("/auth/reset", { replace: true, state });
              }}
            >
              {copy("auth.overlay.choosePassword")}
            </Button>
          ) : null}
          {!isVerifying ? (
            <Button
              variant={
                canOpenBrief || signInApproved || passwordResetReady || passwordChangeApproved
                  ? "secondary"
                  : isSuccess
                    ? "primary"
                    : "ghost"
              }
              onClick={() => {
                dismiss();
                if (
                  !canOpenBrief &&
                  !signInApproved &&
                  !identityChanged &&
                  !passwordResetReady &&
                  !passwordChangeApproved
                ) {
                  goToSignIn();
                }
              }}
            >
              {passwordResetReady
                ? copy("auth.overlay.notNow")
                : canOpenBrief || signInApproved || identityChanged || passwordChangeApproved
                  ? copy("auth.overlay.done")
                  : copy("auth.forgot.back")}
            </Button>
          ) : null}
        </div>
      </section>
    </div>
  );
}
